import { create } from 'zustand';
import { useMutation } from '@tanstack/react-query';
import {
  apiClient,
  setTokens,
  clearTokens,
  getAccessToken,
  getRefreshToken,
} from '@/lib/api-client';
import { clearQueryCache } from '@/lib/query-client';
import type { ApiResponse } from '@/lib/api-client';
import type { ModuleKey } from '@/lib/modules';

/**
 * Authenticated user information stored in client state.
 */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
  tenantId: string;
  mfaEnabled: boolean;
  jobRole: string | null;
  /**
   * Toggleable modules enabled for this user's tenant, from GET /auth/me.
   * May be absent on payloads that do not carry it (e.g. the login response);
   * the store keeps `enabledModules` as `null` until a `/auth/me` payload sets it.
   */
  enabledModules?: ModuleKey[];
}

/**
 * MFA verification state.
 */
export type MfaStatus = 'none' | 'required' | 'verifying' | 'verified';

/**
 * Authentication state managed by Zustand.
 */
interface AuthState {
  /** Current authenticated user, null if not logged in */
  user: AuthUser | null;
  /**
   * Toggleable modules enabled for the tenant, hydrated from GET /auth/me.
   * `null` means "not loaded yet" — consumers treat module-tagged items as
   * visible/allowed until a loaded array says otherwise (no flash-hide).
   */
  enabledModules: ModuleKey[] | null;
  /** Whether the auth state has been initialized (checked on app load) */
  isInitialized: boolean;
  /** Whether the user is authenticated with full access */
  isAuthenticated: boolean;
  /** MFA verification status */
  mfaStatus: MfaStatus;
  /** Set user and mark as authenticated */
  setUser: (user: AuthUser) => void;
  /** Replace the enabled-modules list (e.g. after a fresh /auth/me). */
  setEnabledModules: (modules: ModuleKey[] | null) => void;
  /** Set MFA status */
  setMfaStatus: (status: MfaStatus) => void;
  /** Mark auth state as initialized */
  setInitialized: () => void;
  /** Clear auth state (logout) */
  reset: () => void;
}

/**
 * Zustand store for authentication state.
 * Persists user info in memory (tokens are in localStorage).
 */
export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  enabledModules: null,
  isInitialized: false,
  isAuthenticated: false,
  mfaStatus: 'none',
  setUser: (user) =>
    set((state) => ({
      user,
      isAuthenticated: true,
      mfaStatus: 'verified',
      // Hydrate from the payload when it carries the list (e.g. /auth/me);
      // otherwise keep whatever was previously loaded.
      enabledModules: user.enabledModules ?? state.enabledModules,
    })),
  setEnabledModules: (enabledModules) => set({ enabledModules }),
  setMfaStatus: (mfaStatus) => set({ mfaStatus }),
  setInitialized: () => set({ isInitialized: true }),
  reset: () =>
    set({
      user: null,
      enabledModules: null,
      isAuthenticated: false,
      mfaStatus: 'none',
    }),
}));

/** Login request payload */
interface LoginRequest {
  email: string;
  password: string;
  tenantId: string;
}

/** Login API response */
interface LoginResponse {
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number;
  user: AuthUser;
  mfaRequired: boolean;
}

/** MFA verify request payload */
interface MfaVerifyRequest {
  code: string;
}

/** MFA verify API response */
interface MfaVerifyResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/**
 * Hook providing authentication actions: login, logout, MFA verification.
 *
 * Usage:
 * ```tsx
 * const { login, logout, verifyMfa } = useAuth();
 * login.mutate({ email, password, tenantId });
 * ```
 */
export function useAuth() {
  const { setUser, setMfaStatus, reset } = useAuthStore();

  /**
   * Login mutation — authenticates user with email/password.
   * If MFA is required, sets partial token and mfaStatus to 'required'.
   * Otherwise, stores full tokens and sets user.
   */
  const login = useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const response = await apiClient.post<ApiResponse<LoginResponse>>(
        '/auth/login',
        {
          email: credentials.email,
          password: credentials.password,
          tokenType: 'backoffice',
        },
        {
          headers: {
            'X-Tenant-ID': credentials.tenantId,
          },
        },
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      if (data.mfaRequired) {
        // Store partial token for MFA verification
        if (data.accessToken) {
          setTokens(data.accessToken, '');
        }
        setMfaStatus('required');
      } else {
        // Full authentication — store tokens and user
        if (data.accessToken && data.refreshToken) {
          setTokens(data.accessToken, data.refreshToken);
        }
        setUser(data.user);
      }
    },
  });

  /**
   * MFA verification mutation — validates TOTP code and upgrades to full tokens.
   */
  const verifyMfa = useMutation({
    mutationFn: async (request: MfaVerifyRequest) => {
      setMfaStatus('verifying');
      const response = await apiClient.post<ApiResponse<MfaVerifyResponse>>('/auth/mfa/verify', {
        code: request.code,
      });
      return response.data.data;
    },
    onSuccess: (data) => {
      setTokens(data.accessToken, data.refreshToken);
      // Re-fetch user info from the login response stored in the login mutation
      // The user data was already set during login; just update tokens and status
      setMfaStatus('verified');
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        setUser(currentUser);
      }
    },
    onError: () => {
      setMfaStatus('required');
    },
  });

  /**
   * Logout — invalidates session on server, clears local state and cache.
   */
  const logout = useMutation({
    mutationFn: async () => {
      const refreshToken = getRefreshToken();
      try {
        await apiClient.post('/auth/logout', { refreshToken });
      } catch {
        // Ignore errors during logout — we clear local state regardless
      }
    },
    onSettled: () => {
      clearTokens();
      clearQueryCache();
      reset();
    },
  });

  /**
   * Initialize auth state on app load.
   * Checks if a valid access token exists and attempts to restore the session.
   */
  const initialize = async () => {
    const token = getAccessToken();
    if (!token) {
      useAuthStore.getState().setInitialized();
      return;
    }

    try {
      const response = await apiClient.get<ApiResponse<AuthUser>>('/auth/me');
      setUser(response.data.data);
    } catch {
      // Token is invalid or expired — the interceptor will handle refresh
      // If refresh also fails, tokens are cleared automatically
      clearTokens();
      reset();
    } finally {
      useAuthStore.getState().setInitialized();
    }
  };

  return {
    login,
    logout,
    verifyMfa,
    initialize,
  };
}

/**
 * Listen for session expiry events dispatched by the API client.
 * Call this once at the app root to handle forced logouts.
 */
export function setupSessionExpiryListener() {
  const handler = () => {
    clearTokens();
    clearQueryCache();
    useAuthStore.getState().reset();
  };

  window.addEventListener('auth:session-expired', handler);
  return () => window.removeEventListener('auth:session-expired', handler);
}
